import type { AuthError } from "@app/components/authentication/AuthenticationProvider/APIAuthSetup";
import { clearApplicationState } from "@app/state/cleanup";
import { notifications } from "@lib/notification/notification";
import { ContextualError } from "@lib/utils/ContextualError";
import * as Sentry from "@sentry/browser";
import type { Middleware } from "openapi-fetch";

// Global state for auth providers - will be set after middleware creation
let getAccessToken: (() => Promise<string>) | null = null;
let authErrorHandler: ((error: AuthError) => void) | null = null;

export const setAuthTokenProvider = (tokenProvider: () => Promise<string>) => {
  getAccessToken = tokenProvider;
};

export const setAuthErrorHandler = (errorHandler: (error: AuthError) => void) => {
  authErrorHandler = errorHandler;
};

export const authMiddleware: Middleware = {
  async onRequest({ request }: { request: Request }) {
    try {
      if (!getAccessToken) {
        throw new ContextualError("No access token provider set in auth middleware", {
          request_url: request.url,
          request_method: request.method
        });
      }
      const token = await getAccessToken();
      request.headers.set("Authorization", `Bearer ${token}`);
    } catch (error) {
      console.error("Failed to get access token:", error);

      const authError: AuthError = {
        code: "token_error",
        message: "Failed to obtain access token"
      };

      // Notify error handler which will handle the auth error
      if (authErrorHandler) {
        authErrorHandler(authError);
      }
    }
    return request;
  },

  // TODO: merge this with errorMiddleware https://linear.app/atinary-tech/issue/DEV-1517/merge-authmiddleware-and-errormiddleware-in-front
  // Response interceptor - handle auth and API errors
  onResponse({ response }: { response: Response }) {
    if (response.ok) {
      return;
    }

    switch (response.status) {
      // let the request caller handle the 400
      case 400:
        return;
      case 401: {
        console.error("Authentication error:", response);

        // Clear all application state on auth failure
        void clearApplicationState();

        const authError: AuthError = {
          code: "unauthorized",
          message: "Authentication failed"
        };

        if (authErrorHandler) {
          authErrorHandler(authError);
        }
        break;
      }

      default:
        Sentry.captureException(new Error(`Unhandled API error: ${response.status} ${response.statusText}`));
        console.error("Unhandled API error:", response);
        notifications.error(`An error occurred: ${response.status} ${response.statusText}`);
        return;
    }

    return response;
  }
};
